import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Download, Minus, Plus, RefreshCw } from "lucide-react";
import axios from "axios";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DataTable from "react-data-table-component";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from "@mui/material";
import {
  PaginationDropdown,
  ThinDropdownWithSearch,
} from "../common/GeneralComponent";
import GeneralFooter from "../common/GeneralFooter";
import Loader from "../partials/Loader";
import * as XLSX from "xlsx";
import { handleAuthError } from "../../utils/functions";

const API_URL = process.env.REACT_APP_BASE_URL || "http://localhost:8000";

const MPIVillage = () => {
  const loading = useSelector((state) => state);
  const token = loading?.auth?.token;
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [villages, setVillages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFiltersVisible, setIsFiltersVisible] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [loader, setLoader] = useState(false);

  // Filter states
  const [districts, setDistricts] = useState([]);
  const [tehsils, setTehsils] = useState([]);
  const [dehs, setDehs] = useState([]);
  const [villagesList, setVillagesList] = useState([]);

  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [selectedTehsil, setSelectedTehsil] = useState(null);
  const [selectedDeh, setSelectedDeh] = useState(null);
  const [selectedVillage, setSelectedVillage] = useState(null);

  const [tempDistrict, setTempDistrict] = useState(null);
  const [tempTehsil, setTempTehsil] = useState(null);
  const [tempDeh, setTempDeh] = useState(null);
  const [tempVillage, setTempVillage] = useState(null);

  // Pagination
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalItems: 0,
    totalPages: 0,
  });

  useEffect(() => {
    fetchDistricts();
  }, []);

  const fetchDistricts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/districts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDistricts(response.data.data || []);
    } catch (error) {
      if (handleAuthError(error, dispatch, navigate)) return;
      console.error("Error fetching districts:", error);
    }
  };

  const fetchTehsils = async () => {
    if (!tempDistrict) return;
    try {
      const response = await axios.get(
        `${API_URL}/api/tehsils?district_id=${tempDistrict}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTehsils(response.data.data || []);
    } catch (error) {
      if (handleAuthError(error, dispatch, navigate)) return;
      console.error("Error fetching tehsils:", error);
    }
  };

  const fetchDehs = async () => {
    if (!tempTehsil) return;
    try {
      const response = await axios.get(
        `${API_URL}/api/dehs?tehsil_id=${tempTehsil}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDehs(response.data.data || []);
    } catch (error) {
      if (handleAuthError(error, dispatch, navigate)) return;
      console.error("Error fetching dehs:", error);
    }
  };

  const fetchVillagesList = async () => {
    if (!tempDeh) return;
    try {
      const response = await axios.get(
        `${API_URL}/api/villages?deh_id=${tempDeh}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setVillagesList(response.data.data || []);
    } catch (error) {
      if (handleAuthError(error, dispatch, navigate)) return;
      console.error("Error fetching villages:", error);
    }
  };

  useEffect(() => { fetchTehsils(); }, [tempDistrict]);
  useEffect(() => { fetchDehs(); }, [tempTehsil]);
  useEffect(() => { fetchVillagesList(); }, [tempDeh]);

  const applyFilters = () => {
    if (!tempDistrict) {
      toast.warning("Please select at least a district");
      return;
    }
    setSelectedDistrict(tempDistrict);
    setSelectedTehsil(tempTehsil);
    setSelectedDeh(tempDeh);
    setSelectedVillage(tempVillage);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const clearFilters = () => {
    setSelectedDistrict(null);
    setSelectedTehsil(null);
    setSelectedDeh(null);
    setSelectedVillage(null);
    setTempDistrict(null);
    setTempTehsil(null);
    setTempDeh(null);
    setTempVillage(null);
    setTehsils([]);
    setDehs([]);
    setVillagesList([]);
    setVillages([]);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const calculateVillageWiseMPI = async () => {
    if (!selectedDistrict) {
      toast.warning("Please select at least a district to calculate MPI");
      return;
    }

    Swal.fire({
      title: "Calculate Village-wise MPI?",
      text: "This will calculate MPI for all villages based on your filters",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Calculate",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#137e81",
    }).then(async (result) => {
      if (result.isConfirmed) {
        setIsCalculating(true);
        setLoader(true);
        try {
          let url = `${API_URL}/api/village-wise-mpi/calculate?`;
          if (selectedDistrict) url += `district_id=${selectedDistrict}&`;
          if (selectedTehsil) url += `tehsil_id=${selectedTehsil}&`;
          if (selectedDeh) url += `deh_id=${selectedDeh}&`;
          if (selectedVillage) url += `village_id=${selectedVillage}&`;

          const response = await axios.post(
            url,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );

          if (response.data.success) {
            toast.success(
              `MPI calculated for ${response.data.data.total_villages} villages`
            );
            fetchMPIVillages(1);
          }
        } catch (error) {
          if (handleAuthError(error, dispatch, navigate)) return;
          console.error("Error calculating MPI:", error);
          toast.error("Failed to calculate village-wise MPI");
        } finally {
          setIsCalculating(false);
          setLoader(false);
        }
      }
    });
  };

  const latestRequest = useRef(0);

  const fetchMPIVillages = async (page = 1) => {
    if (!selectedDistrict) return;

    setIsLoading(true);
    setLoader(true);
    try {
      let url = `${API_URL}/api/village-wise-mpi?page=${page}&limit=${pagination.pageSize}`;
      if (selectedDistrict) url += `&district_id=${selectedDistrict}`;
      if (selectedTehsil) url += `&tehsil_id=${selectedTehsil}`;
      if (selectedDeh) url += `&deh_id=${selectedDeh}`;
      if (selectedVillage) url += `&village_id=${selectedVillage}`;

      const requestNumber = ++latestRequest.current;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (requestNumber !== latestRequest.current) return;

      if (response.data.success) {
        setVillages(response.data.data.data || []);
        setPagination((prev) => ({
          ...prev,
          totalPages: response.data.data.pagination.total_pages,
          totalItems: response.data.data.pagination.total_records,
        }));
      }
    } catch (error) {
      if (handleAuthError(error, dispatch, navigate)) return;
      console.error("Error fetching MPI villages:", error);
      toast.error("Failed to fetch village-wise MPI data");
    } finally {
      setIsLoading(false);
      setLoader(false);
    }
  };

  useEffect(() => {
    if (selectedDistrict) {
      fetchMPIVillages(pagination.currentPage);
    }
  }, [
    selectedDistrict,
    selectedTehsil,
    selectedDeh,
    selectedVillage,
    pagination.currentPage,
    pagination.pageSize,
  ]);

  const handlePageSizeChange = (size) => {
    setPagination((prev) => ({ ...prev, pageSize: size, currentPage: 1 }));
  };

  const handlePageChange = (event, value) => {
    setPagination((prev) => ({ ...prev, currentPage: value }));
  };

  const toggleFilters = () => setIsFiltersVisible((prev) => !prev);

  const safeDecimal = (val, places = 4) => {
    const parsed = parseFloat(val);
    return isNaN(parsed) ? (0).toFixed(places) : parsed.toFixed(places);
  };

  const exportToExcel = async () => {
    if (villages.length === 0) {
      toast.warning("No data to export");
      return;
    }

    setIsExporting(true);
    try {
      let url = `${API_URL}/api/village-wise-mpi?limit=999999&page=1`;
      if (selectedDistrict) url += `&district_id=${selectedDistrict}`;
      if (selectedTehsil) url += `&tehsil_id=${selectedTehsil}`;
      if (selectedDeh) url += `&deh_id=${selectedDeh}`;
      if (selectedVillage) url += `&village_id=${selectedVillage}`;

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const exportData = response.data.data.data || [];

      const excelData = exportData.map((v, index) => ({
        "Sr.No": index + 1,
        Village: v.village?.village_name || "N/A",
        District: v.district?.district_name || "N/A",
        Tehsil: v.tehsil?.tehsil_name || "N/A",
        Deh: v.deh?.deh_name || "N/A",
        "# of HHs Surveyed": v.total_hh_surveyed,
        "# of HHs MPI Poor": v.count_poor_hh,
        "% MPI Poor": v.percentage_mpi_poor,
        "# of Individuals (N)": v.total_members_surveyed,
        "Total Poor Individuals (q)": v.total_members_in_poor_hh,
        "Incidence of Poverty (H)": v.incidence_of_poverty_h,
        "Sum Censored Scores": v.sum_censored_scores,
        "Intensity of Poverty (A)": v.intensity_of_poverty_a,
        "MPI (M0)": v.mpi_m0,
        "Ci Years of Schooling": v.ci_years_of_schooling,
        "Ci Child School Attendance": v.ci_child_school_attendance,
        "Ci School Quality": v.ci_school_quality,
        "Ci Health Facility": v.ci_access_to_health_facility,
        "Ci Immunization": v.ci_immunization,
        "Ci Ante-natal Care": v.ci_ante_natal_care,
        "Ci Assisted Delivery": v.ci_assisted_delivery,
        "Ci Drinking Water": v.ci_drinking_water,
        "Ci Sanitation Facility": v.ci_sanitation_facility,
        "Ci Wall Material": v.ci_wall_material,
        "Ci Overcrowding": v.ci_overcrowding,
        "Ci Electricity Access": v.ci_electricity_access,
        "Ci Cooking Fuel": v.ci_cooking_fuel,
        "Ci Assets Ownership": v.ci_assets_ownership,
        "Ci Land & Livestock": v.ci_land_livestock,
      }));

      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(excelData);

      worksheet["!cols"] = [
        { wch: 8 },  { wch: 22 }, { wch: 22 }, { wch: 18 }, { wch: 18 },
        { wch: 18 }, { wch: 18 }, { wch: 14 }, { wch: 20 }, { wch: 24 },
        { wch: 22 }, { wch: 20 }, { wch: 22 }, { wch: 12 },
        { wch: 24 }, { wch: 26 }, { wch: 20 },
        { wch: 20 }, { wch: 18 }, { wch: 20 }, { wch: 20 },
        { wch: 18 }, { wch: 22 }, { wch: 18 }, { wch: 18 },
        { wch: 20 }, { wch: 18 }, { wch: 20 }, { wch: 22 },
      ];

      XLSX.utils.book_append_sheet(workbook, worksheet, "MPI Village");
      const currentDate = new Date().toISOString().split("T")[0];
      XLSX.writeFile(workbook, `MPI_Village_Export_${currentDate}.xlsx`);
      toast.success("Excel file exported successfully");
    } catch (error) {
      if (handleAuthError(error, dispatch, navigate)) return;
      console.error("Error exporting to Excel:", error);
      toast.error("Failed to export Excel file");
    } finally {
      setIsExporting(false);
    }
  };

  const customStyles = {
    headCells: {
      style: {
        border: "1px solid #f3f3f3",
        backgroundColor: "",
        color: "black",
        fontWeight: "bold",
        fontSize: "1.075rem",
        textAlign: "center",
      },
    },
    cells: {
      style: {
        border: "1px solid #f3f3f3",
        textAlign: "center",
      },
    },
  };

  const columns = [
    {
      name: <span className="text-[0.813rem]">Sr.No</span>,
      cell: (row, index) => (
        <span>
          {(pagination.currentPage - 1) * pagination.pageSize + index + 1}
        </span>
      ),
      width: "70px",
      sortable: false,
    },
    {
      name: <span className="text-[0.813rem]">Village</span>,
      selector: (row) => row.village?.village_name || "N/A",
      width: "180px",
      sortable: true,
    },
    {
      name: <span className="text-[0.813rem]">District</span>,
      selector: (row) => row.district?.district_name || "N/A",
      width: "150px",
      sortable: true,
    },
    {
      name: <span className="text-[0.813rem]"># HHs Surveyed</span>,
      selector: (row) => row.total_hh_surveyed,
      width: "130px",
      sortable: true,
    },
    {
      name: <span className="text-[0.813rem]"># HHs Poor</span>,
      selector: (row) => row.count_poor_hh,
      width: "120px",
      sortable: true,
    },
    {
      name: <span className="text-[0.813rem]">% MPI Poor</span>,
      selector: (row) => `${row.percentage_mpi_poor}%`,
      width: "120px",
      sortable: true,
    },
    {
      name: <span className="text-[0.813rem]"># Individuals (N)</span>,
      selector: (row) => row.total_members_surveyed,
      width: "145px",
      sortable: true,
    },
    {
      name: <span className="text-[0.813rem]">Poor Individuals (q)</span>,
      selector: (row) => row.total_members_in_poor_hh,
      width: "155px",
      sortable: true,
    },
    {
      name: <span className="text-[0.813rem]">Incidence (H)</span>,
      selector: (row) => safeDecimal(row.incidence_of_poverty_h),
      width: "130px",
      sortable: true,
    },
    {
      name: <span className="text-[0.813rem]">Censored Scores</span>,
      selector: (row) => safeDecimal(row.sum_censored_scores, 2),
      width: "140px",
      sortable: true,
    },
    {
      name: <span className="text-[0.813rem]">Intensity (A)</span>,
      selector: (row) => safeDecimal(row.intensity_of_poverty_a),
      width: "125px",
      sortable: true,
    },
    {
      name: <span className="text-[0.813rem]">MPI (M0)</span>,
      cell: (row) => (
        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded font-bold text-[0.75em]">
          {safeDecimal(row.mpi_m0)}
        </span>
      ),
      width: "110px",
      sortable: true,
    },
    {
      name: (
        <span className="text-[0.813rem]" title="Ci: Years of Schooling (Education)">
          Ci Yrs Schooling
        </span>
      ),
      cell: (row) => (
        <span className="px-1 py-1 bg-purple-50 text-purple-800 rounded text-[0.75em]">
          {safeDecimal(row.ci_years_of_schooling)}
        </span>
      ),
      width: "145px",
      sortable: true,
    },
    {
      name: (
        <span className="text-[0.813rem]" title="Ci: Child School Attendance (Education)">
          Ci Child Attend
        </span>
      ),
      cell: (row) => (
        <span className="px-1 py-1 bg-purple-50 text-purple-800 rounded text-[0.75em]">
          {safeDecimal(row.ci_child_school_attendance)}
        </span>
      ),
      width: "135px",
      sortable: true,
    },
    {
      name: (
        <span className="text-[0.813rem]" title="Ci: School Quality (Education)">
          Ci Schl Quality
        </span>
      ),
      cell: (row) => (
        <span className="px-1 py-1 bg-purple-50 text-purple-800 rounded text-[0.75em]">
          {safeDecimal(row.ci_school_quality)}
        </span>
      ),
      width: "135px",
      sortable: true,
    },
    {
      name: (
        <span className="text-[0.813rem]" title="Ci: Access to Health Facility (Health)">
          Ci Health Fac.
        </span>
      ),
      cell: (row) => (
        <span className="px-1 py-1 bg-green-50 text-green-800 rounded text-[0.75em]">
          {safeDecimal(row.ci_access_to_health_facility)}
        </span>
      ),
      width: "130px",
      sortable: true,
    },
    {
      name: (
        <span className="text-[0.813rem]" title="Ci: Immunization (Health)">
          Ci Immunization
        </span>
      ),
      cell: (row) => (
        <span className="px-1 py-1 bg-green-50 text-green-800 rounded text-[0.75em]">
          {safeDecimal(row.ci_immunization)}
        </span>
      ),
      width: "135px",
      sortable: true,
    },
    {
      name: (
        <span className="text-[0.813rem]" title="Ci: Ante-natal Care (Health)">
          Ci ANC
        </span>
      ),
      cell: (row) => (
        <span className="px-1 py-1 bg-green-50 text-green-800 rounded text-[0.75em]">
          {safeDecimal(row.ci_ante_natal_care)}
        </span>
      ),
      width: "100px",
      sortable: true,
    },
    {
      name: (
        <span className="text-[0.813rem]" title="Ci: Assisted Delivery (Health)">
          Ci Delivery
        </span>
      ),
      cell: (row) => (
        <span className="px-1 py-1 bg-green-50 text-green-800 rounded text-[0.75em]">
          {safeDecimal(row.ci_assisted_delivery)}
        </span>
      ),
      width: "110px",
      sortable: true,
    },
    {
      name: (
        <span className="text-[0.813rem]" title="Ci: Drinking Water (Standard of Living)">
          Ci Water
        </span>
      ),
      cell: (row) => (
        <span className="px-1 py-1 bg-orange-50 text-orange-800 rounded text-[0.75em]">
          {safeDecimal(row.ci_drinking_water)}
        </span>
      ),
      width: "100px",
      sortable: true,
    },
    {
      name: (
        <span className="text-[0.813rem]" title="Ci: Sanitation Facility (Standard of Living)">
          Ci Sanitation
        </span>
      ),
      cell: (row) => (
        <span className="px-1 py-1 bg-orange-50 text-orange-800 rounded text-[0.75em]">
          {safeDecimal(row.ci_sanitation_facility)}
        </span>
      ),
      width: "120px",
      sortable: true,
    },
    {
      name: (
        <span className="text-[0.813rem]" title="Ci: Wall Material (Standard of Living)">
          Ci Wall
        </span>
      ),
      cell: (row) => (
        <span className="px-1 py-1 bg-orange-50 text-orange-800 rounded text-[0.75em]">
          {safeDecimal(row.ci_wall_material)}
        </span>
      ),
      width: "95px",
      sortable: true,
    },
    {
      name: (
        <span className="text-[0.813rem]" title="Ci: Overcrowding (Standard of Living)">
          Ci Crowding
        </span>
      ),
      cell: (row) => (
        <span className="px-1 py-1 bg-orange-50 text-orange-800 rounded text-[0.75em]">
          {safeDecimal(row.ci_overcrowding)}
        </span>
      ),
      width: "110px",
      sortable: true,
    },
    {
      name: (
        <span className="text-[0.813rem]" title="Ci: Electricity Access (Standard of Living)">
          Ci Electricity
        </span>
      ),
      cell: (row) => (
        <span className="px-1 py-1 bg-orange-50 text-orange-800 rounded text-[0.75em]">
          {safeDecimal(row.ci_electricity_access)}
        </span>
      ),
      width: "120px",
      sortable: true,
    },
    {
      name: (
        <span className="text-[0.813rem]" title="Ci: Cooking Fuel (Standard of Living)">
          Ci Cooking
        </span>
      ),
      cell: (row) => (
        <span className="px-1 py-1 bg-orange-50 text-orange-800 rounded text-[0.75em]">
          {safeDecimal(row.ci_cooking_fuel)}
        </span>
      ),
      width: "105px",
      sortable: true,
    },
    {
      name: (
        <span className="text-[0.813rem]" title="Ci: Assets Ownership (Standard of Living)">
          Ci Assets
        </span>
      ),
      cell: (row) => (
        <span className="px-1 py-1 bg-orange-50 text-orange-800 rounded text-[0.75em]">
          {safeDecimal(row.ci_assets_ownership)}
        </span>
      ),
      width: "100px",
      sortable: true,
    },
    {
      name: (
        <span className="text-[0.813rem]" title="Ci: Land & Livestock (Standard of Living)">
          Ci Land/Livestock
        </span>
      ),
      cell: (row) => (
        <span className="px-1 py-1 bg-orange-50 text-orange-800 rounded text-[0.75em]">
          {safeDecimal(row.ci_land_livestock)}
        </span>
      ),
      width: "140px",
      sortable: true,
    },
  ];

  return (
    <div>
      {loader && <Loader />}
      <div className="my-4 w-full flex flex-col justify-between space-y-3">

        {/* Header */}
        <div className="flex lg:flex-row flex-col lg:justify-between justify-start lg:items-center items-start gap-3 w-full">
          <h1 className="lg:text-[1.475rem] text-[1rem] text-[#212529] font-[Bold]">
            MPI Village
          </h1>
          <div className="flex gap-2">
            <button
              onClick={calculateVillageWiseMPI}
              disabled={isCalculating || !selectedDistrict}
              className={`flex items-center gap-2 px-4 py-1 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors ${
                isCalculating || !selectedDistrict
                  ? "opacity-50 cursor-not-allowed"
                  : ""
              }`}
            >
              <RefreshCw
                size={16}
                className={isCalculating ? "animate-spin" : ""}
              />
              {isCalculating ? "Calculating..." : "Calculate MPI"}
            </button>
            <button
              onClick={exportToExcel}
              disabled={isExporting || villages.length === 0}
              className={`flex items-center gap-2 px-4 py-1 text-sm bg-[#137e81] hover:bg-green-700 text-white rounded-md transition-colors ${
                isExporting || villages.length === 0
                  ? "opacity-50 cursor-not-allowed"
                  : ""
              }`}
            >
              <Download size={16} />
              {isExporting ? "Exporting..." : "Export Excel"}
            </button>
          </div>
        </div>

        {/* Filters — all in one row */}
        <Accordion>
          <AccordionSummary
            expandIcon={
              isFiltersVisible ? (
                <Minus color="#ffff" size={14} />
              ) : (
                <Plus size={14} color="#ffff" />
              )
            }
            onClick={toggleFilters}
            sx={{
              backgroundColor: "#137e81",
              color: "#fff !important",
              "& .MuiAccordionSummary-content": { color: "#fff !important" },
            }}
          >
            Filters
          </AccordionSummary>
          <AccordionDetails>
            {/* Single row — same layout pattern as other pages */}
            <div className="flex flex-row items-end gap-3 w-full flex-wrap">
              <div className="flex-1 min-w-[150px]">
                <ThinDropdownWithSearch required={false}
                  label="District"
                  options={districts?.map((d) => ({
                    value: d.district_id,
                    label: d.district_name,
                  }))}
                  value={tempDistrict}
                  onChange={(val) => {
                    setTempDistrict(val);
                    setTempTehsil(null);
                    setTempDeh(null);
                    setTempVillage(null);
                    setTehsils([]);
                    setDehs([]);
                    setVillagesList([]);
                  }}
                />
              </div>
              <div className="flex-1 min-w-[150px]">
                <ThinDropdownWithSearch required={false}
                  label="Tehsil"
                  options={tehsils?.map((d) => ({
                    value: d.tehsil_id,
                    label: d.tehsil_name,
                  }))}
                  value={tempTehsil}
                  onChange={(val) => {
                    setTempTehsil(val);
                    setTempDeh(null);
                    setTempVillage(null);
                    setDehs([]);
                    setVillagesList([]);
                  }}
                />
              </div>
              <div className="flex-1 min-w-[150px]">
                <ThinDropdownWithSearch required={false}
                  label="Deh"
                  options={dehs?.map((d) => ({
                    value: d.deh_id,
                    label: d.deh_name,
                  }))}
                  value={tempDeh}
                  onChange={(val) => {
                    setTempDeh(val);
                    setTempVillage(null);
                    setVillagesList([]);
                  }}
                />
              </div>
              <div className="flex-1 min-w-[150px]">
                <ThinDropdownWithSearch required={false}
                  label="Village"
                  options={villagesList?.map((d) => ({
                    value: d.village_id,
                    label: d.village_name,
                  }))}
                  value={tempVillage}
                  onChange={setTempVillage}
                />
              </div>
              <div className="flex gap-2 items-end pb-0.5">
                <button
                  onClick={clearFilters}
                  className="px-4 py-2 bg-[#be9609] text-white rounded-lg hover:bg-[#997800] text-sm"
                >
                  Clear
                </button>
                <button
                  onClick={applyFilters}
                  className="px-4 py-2 bg-[#137e81] text-white rounded-lg hover:bg-[#0f5f5f] text-sm"
                >
                  Apply
                </button>
              </div>
            </div>
          </AccordionDetails>
        </Accordion>

        {/* Ci Legend — Education / Health / Standard of Living */}
        {villages.length > 0 && (
          <div className="flex flex-row items-center gap-3 px-2 flex-wrap">
            <span className="text-xs text-gray-500 font-semibold self-center">
              Ci Legend:
            </span>
            <span className="px-3 py-1 bg-purple-50 text-purple-800 rounded text-xs font-medium border border-purple-200">
              Education
            </span>
            <span className="px-3 py-1 bg-green-50 text-green-800 rounded text-xs font-medium border border-green-200">
              Health
            </span>
            <span className="px-3 py-1 bg-orange-50 text-orange-800 rounded text-xs font-medium border border-orange-200">
              Standard of Living
            </span>
          </div>
        )}

        {/* Table */}
        <div className="bg-white rounded-lg shadow w-full lg:mb-[30px] mt-2 mb-[7rem]">
          <div className="p-4 flex md:flex-row rounded-t-lg gradiant2 flex-col gap-3 justify-between md:items-center border-lightGray border-b">
            <div>
              <h1 className="lg:text-[0.875rem] text-[13px] tracking-[0.0625rem] text-[#856800] font-[bold] uppercase">
                MPI Village-wise List
              </h1>
              <p className="text-[0.7rem] text-gray-500 mt-0.5">
                Ci = Censored Headcount Ratio (Poor & Deprived HHs ÷ Total HHs Surveyed)
              </p>
            </div>
          </div>
          <div className="p-[1.25rem] w-full">
            <div className="flex lg:flex-row flex-col lg:justify-between justify-start gap-2 lg:items-center items-start mb-4 w-full">
              <PaginationDropdown
                value={pagination.pageSize}
                onChange={handlePageSizeChange}
                options={[
                  { value: 10, label: "10" },
                  { value: 20, label: "20" },
                  { value: 30, label: "30" },
                ]}
              />
              {villages.length > 0 && (
                <span className="text-xs text-gray-400">
                  Showing {villages.length} of {pagination.totalItems} villages
                </span>
              )}
            </div>

            {villages.length === 0 && !isLoading ? (
              <div className="text-center py-12">
                <p className="text-gray-500 text-lg">
                  {selectedDistrict
                    ? "No village-wise MPI data found. Try calculating MPI first."
                    : "Please select filters and apply to view village-wise MPI data"}
                </p>
              </div>
            ) : (
              <>
                <DataTable
                  columns={columns}
                  persistTableHead
                  data={villages}
                  customStyles={customStyles}
                  highlightOnHover
                  progressPending={isLoading}
                />
                <GeneralFooter
                  pagination={pagination}
                  handlePageChange={handlePageChange}
                  handlePageSizeChange={handlePageSizeChange}
                  dataLength={villages.length}
                />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MPIVillage;